import * as fb from './filebase';
import * as sharepoint from './filebaseSharepoint';
import { StateTypeContext } from '../clientportal/types';
import { ActivityCenterContextType } from "../../Context/ActivityCenter";
import { runInnerFunction } from '../../compatManager';
import { folderSpecialChars, SortDir } from './FileDrive';
import { intervalToDuration, parseISO } from 'date-fns';
import { getCookie } from '../topbars/topBarFuncs';
import moment from 'moment';
import { ArchiveData } from './filebase';

// Legacy window global injected by the datamodule bootstrap (see commonFormatFuncs.d.ts / index.html).
// Only ForceDesktop is read here; when true, documents open in desktop Word instead of Word Online.
declare const lddGlobalSettings: { ForceDesktop?: boolean } | undefined;

export interface lddDriveObj {
    type: number,
    sharepointConfig?: sharepoint.SPFileSystemConfig,
}

export function hasSpecialCharacter(value: string, charArray: string[]): boolean {
    let ret = false;
    for (let i = 0; i < charArray.length; i++) {
        if (value.indexOf(charArray[i]) !== -1) {
            ret = true;
            break;
        }
    }
    return ret;
}

export function replaceMatterNumberSpecialChar(fileNum: string): string {
    const RegExpSpeciaChar = [".", "?", "/", "\\", "+", "*", "$", "^", "|", "!", "(", ")", "[", "]", "{", "}", ":"];
    folderSpecialChars.forEach(c => {
        let str = "";
        for (let i = 0; i < c.length; i++) {
            if (RegExpSpeciaChar.indexOf(c[i]) !== -1) {
                str += "\\";
            }
            str += c[i];
        }
        const r = new RegExp(str, "g");
        fileNum = fileNum.replace(r, "-");
    })
    fileNum = fileNum.trim();
    return fileNum
}


// Global reference to FileDrive state for external access
export const globalFileDriveState: {
    drive: fb.FileSystem | null;
    currentFolder: fb.FolderEntry | null;
    activityCenter: ActivityCenterContextType | null;
} = {
    drive: null,
    currentFolder: null,
    activityCenter: null
};

export namespace FileDrive_Funcs {
    let myDrive: fb.FileSystem; // file system connection
    let currentFolder: fb.FolderEntry; // the active drive folder
    let mainFolder: fb.FolderEntry; // the matter folder
    let matterNumber: string;
    let setLoadingState;
    let setFolderContent;
    let activityCenter: ActivityCenterContextType;
    // Callback set by the email composer (via the topWindowOpenMyDriveAttach bridge) that
    // receives the picked file's name/ext/temp-path so it can be attached to the open email.
    let emailAttachCallback: ((docName: string, docExt: string, docPath: string) => void) | null = null;

    export function setEmailAttachCallback(cb: (docName: string, docExt: string, docPath: string) => void): void {
        emailAttachCallback = cb;
    }

    export async function initializeDrive(config: lddDriveObj, matterNumber: string, activityCenter: ActivityCenterContextType): Promise<fb.FileSystem> {
        const drive = createLddDrive(config, matterNumber, undefined, undefined, activityCenter);
        await getMainFolder();
        return drive;
    }

    export function createLddDrive(config: lddDriveObj, fileNum: string, setIsLoading: (value: boolean) => void = undefined, setFolderContentInitial: (value: Array<fb.FileEntry | fb.FolderEntry>) => void = undefined, setActivityCenter: ActivityCenterContextType): fb.FileSystem {
        if (config.type === 1) {  // need to create a new connection for each drive type instead of reusing existing connection
            myDrive = createSharepointFileSystem(config.sharepointConfig)
        }
        currentFolder = myDrive.root;
        mainFolder = undefined;

        matterNumber = replaceMatterNumberSpecialChar(fileNum);
        setLoadingState = setIsLoading;
        setFolderContent = setFolderContentInitial;
        activityCenter = setActivityCenter;
        
        // Update global state for external access
        globalFileDriveState.drive = myDrive;
        globalFileDriveState.currentFolder = currentFolder;
        globalFileDriveState.activityCenter = activityCenter;
        
        return myDrive;
    }

    export function setExistingDrive(drive: fb.FileSystem, folder: fb.FolderEntry, setIsLoading: (value: boolean) => void = undefined, setFolderContentInitial: (value: Array<fb.FileEntry | fb.FolderEntry>) => void = undefined, setActivityCenter: ActivityCenterContextType): void {
        myDrive = drive;
        currentFolder = folder;
        mainFolder = folder;

        setLoadingState = setIsLoading;
        setFolderContent = setFolderContentInitial;
        activityCenter = setActivityCenter;
        
        // Trigger initial folder content load
        refreshFolder();
    }
    
    export function returnCurrentFolder(): fb.FolderEntry {
        return currentFolder;
    }

    export function isMainFolder(): boolean {
        var ret = false;
        if (mainFolder.fullPath === currentFolder.fullPath) {
            ret = true;
        }
        return ret;
    }

    export function drawPath(): string {
        var ret = "";
        if (currentFolder) {
            ret = currentFolder.buildSubPath();
            ret = ret.substring(`\\${myDrive.config.rootDrive}`.length)
            ret = ret.replace(/\//g, "ldd_tomek_ldd");
            ret = ret.replace(/ldd_tomek_ldd/g, " / ");
        }
        return ret;

    }

    export async function refreshFolder(): Promise<void> {

        try {
            setLoadingState(true);
            if (mainFolder === undefined) {
                await getMainFolder();
            } else {
                getMyFolderContent();
            }
        } catch (e) {
            //lddActivityCenterAdd(e, null, "error", null)
        }
        if (setLoadingState) {
            setLoadingState(false);
        }
    }

    export async function getMainFolder(): Promise<void> {
        try {
            setLoadingState(true);
            if (mainFolder === undefined) {
                await getFolder(matterNumber);
                mainFolder = currentFolder;
            }
            currentFolder = mainFolder;
            await getMyFolderContent();
        } catch (e) {
            //lddActivityCenterAdd(e, null, "error", null)
        }
        if (setLoadingState) {
            setLoadingState(false);
        }
    }

    function createSharepointFileSystem(config: sharepoint.SPFileSystemConfig): fb.FileSystem {
        return new sharepoint.FileSystem(config);
    }

    export async function getParentFolder(): Promise<void> {
        try {
            setLoadingState(true);
            if (!isMainFolder()) {
                currentFolder = await currentFolder.getParent();
            }
            await getMyFolderContent();
        } catch (e) {
            //lddActivityCenterAdd(e, null, "error", null)
        }
        setLoadingState(false);
    }

    export async function getFolder(path: string): Promise<void> {
        try {
            setLoadingState(true);
            currentFolder = await currentFolder.getFolder(path, { create: true });
            await getMyFolderContent();

        } catch (e) {
            console.log(e)
            //lddActivityCenterAdd(e, null, "error", null)
        }
        setLoadingState(false);
    }

    function parseFolderContentBySortCookie(contentList: any[]) {
        const nameSortCookie = getCookie("myDriveDocNameSort");
        const modifiedDateSortCookie = getCookie("myDriveDocModifiedDateSort");

        if (modifiedDateSortCookie === SortDir.None && nameSortCookie !== SortDir.None) {
            contentList = nameSortCookie === SortDir.Up ? sortFolderContentByNameAsc(contentList) : sortFolderContentByNameDsc(contentList);
        } else if (nameSortCookie === SortDir.None && modifiedDateSortCookie !== SortDir.None) {
            contentList = modifiedDateSortCookie === SortDir.Up ? sortFolderContentByModifiedDateAsc(contentList) : sortFolderContentByModifiedDateDsc(contentList);
        }

        return contentList;
    }

    export async function getMyFolderContent(): Promise<Array<fb.FileEntry | fb.FolderEntry>> {
        let List = [];
        if (currentFolder && currentFolder !== myDrive.root) {
            List = await currentFolder.getContentList();
        }
        
        List = parseFolderContentBySortCookie(List);
        if (setFolderContent) {
            setFolderContent(List);
        }
        return List
    }

    export async function shareClientConnect(file: fb.FileEntry, portalService, state: StateTypeContext): Promise<void> {
        if (!(state.portalId > "")) {
            activityCenter.Add(`Please initiate Client Connect portal first.`, null, "error", null);
            return;
        }
        const msgID = "shareClientConnect";

        try {
            var docName = file.name;
            activityCenter.Add(`Sharing document '${docName}'.`, null, "loading", msgID);
            var blob = await file.getContent("blob", "pdf") as Blob;
            if (blob.type === 'application/pdf' && blob.size > 50) {
                docName = `${docName.substring(0, docName.indexOf("."))}.pdf`;
                var buff = await blob.arrayBuffer();
                await portalService.uploadDocument(buff, state.portalId, state.appId, docName, 'CPL');
                activityCenter.Add(`'${docName}' shared to Client Connect.`, null, "success", null);
            } else {
                throw new Error("Can't get Blob");
            }
        } catch (e) {
            activityCenter.Add(`Error sharing '${docName}' ${e}.`, null, "error", null);
        }
        activityCenter.Delete(msgID);
    }


    export async function shareRemoteSigning(file: fb.FileEntry, portalService, state: StateTypeContext): Promise<void> {
        if (!(state.portalId > "")) {
            activityCenter.Add(`Please initiate Client Connect portal first.`, null, "error", null);
            return;
        }
        const msgID = "shareRemoteSigning";
        try {
            var docName = file.name;
            activityCenter.Add(`Sharing document '${docName}'.`, null, "loading", msgID);
            var blob = await file.getContent("blob", "pdf") as Blob;
            if (blob.type === 'application/pdf' && blob.size > 50) {
                docName = `${docName.substring(0, docName.indexOf("."))}.pdf`;
                var buff = await blob.arrayBuffer();
                await portalService.uploadDocument(buff, state.portalId, state.appId, docName, 'DefaultRSPFiles');
                activityCenter.Add(`'${docName}' shared to Remote Signing.`, null, "success", null);
            } else {
                throw new Error("Can't get Blob");
            }
        } catch (e) {
            activityCenter.Add(`Error sharing '${docName}' ${e}.`, null, "error", null);
        }
        activityCenter.Delete(msgID);
    }

    export async function downloadDocument(file: fb.FileEntry): Promise<void> {
        var msgID = "downloadDocument" + new Date().toISOString();
        let docName = file.name;
        try {
            activityCenter.Add(`Downloading document '${docName}'.`, null, "loading", msgID);

            const blob = (await file.getContent("blob")) as Blob;
            let url:string = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = docName;
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(link.href), 7000);
        } catch (e) {
            activityCenter.Add(`Error downloading '${docName}' ${e}.`, null, "error", null);
        }
        activityCenter.Delete(msgID);
    }

    export async function downloadDocumentAsPDF(file: fb.FileEntry): Promise<void> {
        let msgID = "downloadDocumentAsPDF" + new Date().toISOString();
        let docName = file.name;
        try {
            activityCenter.Add(`Downloading document '${docName}'.`, null, "loading", msgID);
            const blob = await file.getContent("blob", "pdf") as Blob;
            if (blob.type === 'application/pdf' && blob.size > 50) {
                docName = `${docName.substring(0, docName.indexOf("."))}.pdf`;
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = docName;
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(link.href), 7000);
            } else {
                throw new Error("Can't get Blob");
            }
        } catch (e) {
            activityCenter.Add(`Error downloading '${docName}' ${e}.`, null, "error", null);
        }
        activityCenter.Delete(msgID);
    }

    export async function getDocumentAsPDFBlob(file: fb.FileEntry): Promise<Blob | null> {
        try {
            const blob = await file.getContent("blob", "pdf") as Blob;
            if (blob.type === 'application/pdf' && blob.size > 50) {
                return blob;
            } else {
                throw new Error("Can't get PDF Blob");
            }
        } catch (e) {
            console.error('Error getting PDF blob:', e);
            return null;
        }
    }

    export async function openDocument(file: fb.FileEntry, appCode?: any, userID?: any, appId?: any, sessionID?: string,): Promise<void> {
        const docName = file.name;
        var msgID = "openDocument" + new Date().toISOString();
        activityCenter.Add(`Opening document '${docName}'.`, null, "loading", msgID);
        try {
            
        if (docName.endsWith('.pdf')) {
            const path = file.buildSubPath();
            const pdfViewerUrl = `${window.location.protocol}//${window.location.host}/pdfEditor?url=${encodeURIComponent(path)}&appCode=${appCode}&userID=${userID}&appId=${appId}&SessionID=${sessionID}`;
            const newWindow = window.open(pdfViewerUrl, "_blank");
            if (newWindow) {
                newWindow.onload = () => newWindow.document.title = docName;
            }
        } else if (lddGlobalSettings?.ForceDesktop) {
            // Experimental: force the OS to open the file in desktop Word
            // readMode=false so it opens for editing. Toggle via lddGlobalSettings.ForceDesktop.
            await file.openDocument(false, true);
        } else {
            const link = await file.getShareableUrl();
            window.open(link, "_blank");
        }
    
        activityCenter.Delete(msgID);
        } catch(e){
            activityCenter.Add(`Error opening document '${docName}'.`, null, "error", msgID);        }
    }
    

    export function printDateFromNow(item: fb.FileEntry | fb.FolderEntry): string {
        const currDate = new Date();
        const timeDifference = intervalToDuration({start: currDate, end: parseISO(item.modifiedDate)});
        if (timeDifference.days >= 30 || timeDifference.months >= 1 || timeDifference.years >= 1) {
            return moment(item.modifiedDate).format('LL');
        } else if (timeDifference.days >= 1) {
            return moment(item.modifiedDate).startOf('day').fromNow();
        } else {
            if (timeDifference.hours >= 1) {
                return moment(item.modifiedDate).startOf('hour').fromNow();
            } else {
                return moment(item.modifiedDate).startOf('minute').fromNow();
            }
        }
    }

    export function sortFolderContentByNameAsc(folderContent: any[]) {
        return folderContent.sort((a,b) => a.name.localeCompare(b.name));
    }

    export function sortFolderContentByNameDsc(folderContent: any[]) {
        return folderContent.sort((a,b) => b.name.localeCompare(a.name));
    }

    export function sortFolderContentByModifiedDateAsc(folderContent: any[]) {
        return folderContent.sort((a,b) => new Date(b.modifiedDate).getTime() - new Date(a.modifiedDate).getTime());
    }

    export function sortFolderContentByModifiedDateDsc(folderContent: any[]) {
        return folderContent.sort((a,b) => new Date(a.modifiedDate).getTime() - new Date(b.modifiedDate).getTime());
    }

    export async function deleteDocument(file: fb.FileEntry): Promise<void> {
        const docName = file.name;
        try {
            await file.remove();
            activityCenter.Add(`Document '${docName}' deleted successfully`, null, "success", null);
        } catch(e){
            activityCenter.Add(`${e}`, null, "error", null);

        }
        refreshFolder();
    }

    export async function deleteFolder(folder: fb.FolderEntry): Promise<void> {
        const docName = folder.name;
        let content = await folder.getContentList();
        if (content.length === 0) {
            await folder.remove();
            activityCenter.Add(`Folder '${docName}' deleted successfully`, null, "success", null);
        } else {
            activityCenter.Add(`Folder '${docName}' can not be deleted because it has content inside.`, null, "error", null);
        }
        refreshFolder();
    }

    // Convert a drive file to a PDF blob and upload it to a server temp file; returns the temp file path.
    async function uploadDriveFileAsTempPdf(file: fb.FileEntry, SessionID: string): Promise<string> {
        const blob = await file.getContent("blob", "pdf") as Blob;
        if (!(blob.type === 'application/pdf' && blob.size > 50)) {
            throw new Error("Can't get Blob");
        }
        const rt = await fetch(`uploadTempFile.asp?SessionID=${SessionID}`, {
            method: "POST",
            mode: 'cors',
            headers: {
                "Content-Type": blob.type,
            },
            body: blob
        });
        return await rt.text();
    }

    export async function emailDocument(file: fb.FileEntry, SessionID: string): Promise<void> {
        var msgID = "emailDocument" + new Date().toISOString();
        const docName = file.name;
        try {
            activityCenter.Add(`Retrieving document '${docName}'.`, null, "loading", msgID);
            const path = await uploadDriveFileAsTempPdf(file, SessionID);
            runInnerFunction('ReactShell.callEmailDriveDocument', [file.justName, "pdf", path]);
        } catch (e) {
            activityCenter.Add(`Error retrieving '${docName}' ${e}.`, null, "error", null);
        }
        activityCenter.Delete(msgID);
    }

    // Same idea as emailDocument, but hands the temp file to the already-open email composer
    // (via the callback set by setEmailAttachCallback) instead of opening a new email. Word/PDF
    // files are converted to PDF (same as the existing email flow); zip/txt files are attached
    // as-is. uploadTempFile.asp stores raw bytes and SendEmail.asp renames the temp file using
    // doc_name + docExt on send, so the recipient gets a properly-named .zip / .txt.
    export async function attachDriveDocumentToEmail(file: fb.FileEntry, SessionID: string): Promise<void> {
        var msgID = "attachDriveDocumentToEmail" + new Date().toISOString();
        const docName = file.name;
        try {
            activityCenter.Add(`Retrieving document '${docName}'.`, null, "loading", msgID);
            const convertToPdf = file.ext === 'docx' || file.ext === 'pdf';
            const blob = await file.getContent("blob", convertToPdf ? "pdf" : "") as Blob;
            if (convertToPdf && (blob.type !== 'application/pdf' || blob.size <= 50)) {
                throw new Error("Can't get Blob");
            }
            const rt = await fetch(`uploadTempFile.asp?SessionID=${SessionID}`, {
                method: "POST",
                mode: 'cors',
                headers: { "Content-Type": blob.type || 'application/octet-stream' },
                body: blob
            });
            const path = await rt.text();
            const docExt = convertToPdf ? "pdf" : file.ext;
            if (emailAttachCallback) {
                emailAttachCallback(file.justName, docExt, path);
            }
        } catch (e) {
            activityCenter.Add(`Error retrieving '${docName}' ${e}.`, null, "error", null);
        } finally {
            emailAttachCallback = null;
            activityCenter.Delete(msgID);
        }
    }

    export async function getArchiveFiles(AppCode: string, UserID: string , AppID: string, SessionID:string, matterNumber: string): Promise<Array<ArchiveData>>{
        const RWAPI = `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${AppCode}/${UserID}/${AppID}`

        // Send the matter (sanitized with the SAME cleaner that named the SharePoint folder) so the
        // backend can scan the new {userId}/{matter}/ blob prefix. Blank matter -> legacy-only read.
        const matterParam = matterNumber && matterNumber.trim()
            ? `&matter=${encodeURIComponent(replaceMatterNumberSpecialChar(matterNumber))}`
            : "";

        const res = await fetch(`${RWAPI}/filedrive/getArchiveFiles?SessionID=${SessionID}${matterParam}`, {
            method: "GET"
        });

        if (!res.ok){
            throw new Error(`Failed to fetch archive files: ${res.status}`);
        }

        const data: ArchiveData[] = await res.json();
        return data;
    }
}
